import React from "react";
import { Drawer, Tag, InputNumber, Space, Typography, Tooltip, Avatar, Flex } from "antd";
import { useTranslation } from "react-i18next";
import ResponsiveTable from "../../../design-system/ResponsiveTable";
import { useIsMobile } from "../../../design-system/useMediaQuery";

const { Text } = Typography;

const hours = h => `${(h || 0).toFixed(1)}h`;

/**
 * Planned hours (from the calendar) vs. recorded attendance per employee and week,
 * with overtime and working-time rule violations. `rules` are editable limits.
 */
const WorkloadDrawer = ({ open, onClose, rows, actuals, rules, onRulesChange, periodLabel }) => {
  const { t } = useTranslation("dashboard");
  const isMobile = useIsMobile();

  const ruleInput = (key, label) => (
    <Space size={4}>
      <Text type="secondary">{label}</Text>
      <InputNumber
        size="small"
        min={0}
        max={168}
        value={rules[key]}
        onChange={value => value !== null && onRulesChange({ ...rules, [key]: value })}
      />
    </Space>
  );

  const columns = [
    {
      title: t("Employee"),
      key: "employee",
      fixed: "left",
      mobile: "title",
      render: (_, row) => (
        <Flex gap={8} align="center">
          <Avatar size="small" src={row.employee?.picture?.url || null}>
            {row.employee?.firstName?.substring(0, 2).toUpperCase()}
          </Avatar>
          {row.employee?.firstName} {row.employee?.lastName}
        </Flex>
      ),
      sorter: (a, b) =>
        `${a.employee.firstName} ${a.employee.lastName}`.localeCompare(
          `${b.employee.firstName} ${b.employee.lastName}`
        )
    },
    { title: t("Week"), dataIndex: "week", sorter: (a, b) => a.week.localeCompare(b.week) },
    {
      title: t("Planned"),
      dataIndex: "planned",
      render: hours,
      sorter: (a, b) => a.planned - b.planned
    },
    {
      title: t("Actual"),
      key: "actual",
      render: (_, row) => {
        const actual = actuals?.[row.employee._id]?.[row.week];
        return actual === undefined ? <Text type="secondary">–</Text> : hours(actual);
      }
    },
    {
      title: t("Difference"),
      key: "difference",
      render: (_, row) => {
        const actual = actuals?.[row.employee._id]?.[row.week];
        if (actual === undefined) return <Text type="secondary">–</Text>;
        const diff = actual - row.planned;
        return (
          <Text type={Math.abs(diff) >= 1 ? "warning" : undefined}>
            {diff > 0 ? "+" : ""}
            {hours(diff)}
          </Text>
        );
      }
    },
    {
      title: t("Overtime"),
      dataIndex: "overtime",
      render: (value, row) =>
        row.overMax ? (
          <Tooltip title={`${t("Above the legal maximum of")} ${rules.maxWeeklyHours}h`}>
            <Tag color="red">{hours(value)}</Tag>
          </Tooltip>
        ) : value > 0 ? (
          <Tag color="orange">{hours(value)}</Tag>
        ) : (
          <Text type="secondary">–</Text>
        ),
      sorter: (a, b) => a.overtime - b.overtime
    },
    {
      title: t("Rule violations"),
      key: "issues",
      mobile: "subtitle",
      render: (_, row) => {
        const issues = [...row.dailyViolations, ...row.restViolations];
        if (issues.length === 0) return <Tag color="green">{t("OK")}</Tag>;
        const tags = (
          <Space size={4} wrap>
            {row.dailyViolations.length > 0 && (
              <Tag color="red">
                {row.dailyViolations.length}× {t("long day")}
              </Tag>
            )}
            {row.restViolations.length > 0 && (
              <Tag color="volcano">
                {row.restViolations.length}× {t("short rest")}
              </Tag>
            )}
          </Space>
        );
        // Phones have no hover: list the violations under the tags instead of in a tooltip
        if (isMobile) {
          return (
            <Space direction="vertical" size={4}>
              {tags}
              <Text type="secondary" style={{ whiteSpace: "pre-line", fontSize: 12 }}>
                {issues.join("\n")}
              </Text>
            </Space>
          );
        }
        return (
          <Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{issues.join("\n")}</div>}>
            {tags}
          </Tooltip>
        );
      }
    }
  ];

  return (
    <Drawer
      title={`${t("Workload")} · ${periodLabel}`}
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : "min(980px, 100vw)"}
    >
      <Space wrap size={[16, 8]} style={{ marginBottom: 16 }}>
        {ruleInput("weeklyHours", t("Contract h/week"))}
        {ruleInput("maxWeeklyHours", t("Max h/week"))}
        {ruleInput("maxDailyHours", t("Max h/day"))}
        {ruleInput("minRestHours", t("Min rest h"))}
      </Space>
      <ResponsiveTable
        size="small"
        rowKey="key"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 800 }}
        locale={{ emptyText: t("No planned shifts in this period") }}
      />
      <Text type="secondary" style={{ display: "block", marginTop: 12 }}>
        {t(
          "Actual hours come from attendance check-ins. Defaults follow the German Working Hours Act (ArbZG)."
        )}
      </Text>
    </Drawer>
  );
};

export default WorkloadDrawer;
